import React, { useEffect, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import { encryptGeminiKey } from '../services/geminiService';
import XIcon from './icons/XIcon';
import SparklesIcon from './icons/SparklesIcon';

interface GeminiApiKeyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (updates: { encryptedGeminiKey: string }) => Promise<void>;
    language?: Language;
}

const GeminiApiKeyModal: React.FC<GeminiApiKeyModalProps> = ({
    isOpen,
    onClose,
    onSave,
    language,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [geminiKeyInput, setGeminiKeyInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setGeminiKeyInput('');
        setError('');
        setIsSaving(false);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSave = async () => {
        const trimmed = geminiKeyInput.trim();
        if (!trimmed) return;
        setIsSaving(true);
        setError('');
        try {
            const encryptedKey = await encryptGeminiKey(trimmed);
            await onSave({ encryptedGeminiKey: encryptedKey });
            onClose();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to save key.';
            setError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[650] flex items-center justify-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20 dark:border-slate-800"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-6 h-6 text-brand-orange" />
                        <h3 className="font-black text-slate-800 dark:text-slate-100 tracking-tight text-lg">
                            {t('enableAiFeatures')}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t('personalGeminiKeyDesc')}
                    </p>
                    <div className="bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 rounded-xl p-4 space-y-2">
                        <p className="text-xs font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
                            {t('geminiKeyHowToTitle')}
                        </p>
                        <ol className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed space-y-1.5 list-decimal list-inside">
                            <li>
                                {t('geminiKeyHowToStep1Open')} (
                                <a
                                    href="https://aistudio.google.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sky-700 dark:text-sky-300 underline font-bold hover:opacity-80"
                                >
                                    aistudio.google.com
                                </a>
                                )
                            </li>
                            <li>{t('geminiKeyHowToStep2')}</li>
                            <li>{t('geminiKeyHowToStep3')}</li>
                            <li>{t('geminiKeyHowToStep4')}</li>
                        </ol>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
                        {t('personalGeminiKeyPrivacy')}
                    </p>
                    <input
                        type="password"
                        value={geminiKeyInput}
                        onChange={(e) => setGeminiKeyInput(e.target.value)}
                        placeholder="AIza..."
                        className="w-full p-3 border border-slate-300 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-brand-orange bg-slate-50 dark:bg-slate-800 dark:text-white outline-none font-mono text-sm"
                    />
                    {error && (
                        <p className="text-xs text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-xl border border-red-100 dark:border-red-900/30">
                            {error}
                        </p>
                    )}
                </div>

                <div className="p-6 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!geminiKeyInput.trim() || isSaving}
                        className="flex-[2] py-3 bg-brand-orange text-white font-black rounded-xl shadow-lg shadow-brand-orange/20 hover:opacity-90 disabled:opacity-50 transition-all text-xs uppercase tracking-widest"
                    >
                        {isSaving ? '...' : t('saveKey')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GeminiApiKeyModal;
